import React, { useEffect, useState, Fragment } from 'react';
import { filter, find } from 'ramda';
import Button from '~~elements/Button';
import Modal from '~~elements/Modal';
import Multilingual from '~~utils/Multilingual';
import { SchedulePanelDiv, ModalFilterDiv, ListModeDiv } from '../styled';
import moment from 'moment';

const ScheduleLocation = ({
  tournament,
  games,
  groups,
  gameLocations,
  handleGameFilterOption,
  handleGameFilterDateRange,
  selectedGroups,
  selectedDateRange,
  setGameListCollapse,
  gameListCollapse,
  setSelectedGame,
  handleTryCreateGame,
}) => {
  const [locationGames, setLocationGames] = useState({});

  const [isGameFilterOpen, setGameFilterOpen] = useState(false);

  useEffect(() => {
    const getMatchedGames = (locationUniqid) => {
      return filter((g) => g.info.location_uniqid === locationUniqid, games);
    };

    const locating = {};
    gameLocations.forEach((location) => {
      locating[location.uniqid] = getMatchedGames(location.uniqid);
      return;
    });

    setLocationGames(locating);
  }, [games, gameLocations]);

  const handleGameFilterClick = () => {
    setGameFilterOpen(!isGameFilterOpen);
  };

  const handleLocationCollapse = () => {
    setGameListCollapse(!gameListCollapse);
  };

  const getGamesInLocation = (locationUniqid) => {
    const matchFilter = (game) => {
      if (selectedGroups.length > 0 && !selectedGroups.includes(game.group)) {
        return false;
      }

      const startDate = selectedDateRange[0];
      const endDate = selectedDateRange[1];
      const gameTime = new Date(game.info.time.scheduled_start_at);
      if (startDate && moment(gameTime).isBefore(startDate)) {
        return false;
      }
      if (endDate && moment(endDate).isBefore(gameTime)) {
        return false;
      }
      return true;
    };

    const getTeamClassName = (game, side) => {
      if (game.info.status !== 'FINISHED') {
        return '';
      }

      return game.info.winner_side === side ? 'win' : 'lose';
    };

    const handleGameClick = (gameUniqid) => () => {
      setSelectedGame(gameUniqid);
    };

    return filter(matchFilter, locationGames[locationUniqid])
      .reverse()
      .map((game) => (
        <div
          key={game.uniqid}
          className={`list-mode-item--game ${game.info.status === 'FINISHED' ? 'finished' : ''}`}
          onClick={handleGameClick(game.uniqid)}
        >
          <div>
            <h4>{`G${game.seq}`}</h4>
            <label>&nbsp;</label>
            <label>{moment(game.info.time.scheduled_start_at).format('MM/DD HH:mm')}</label>
          </div>
          <div>
            <label className={getTeamClassName(game, 'AWAY')}>
              {!game.away.team ? Multilingual(`GAME.TEAM_NONE`) : game.away.team}
            </label>
            <label>&nbsp;/&nbsp;</label>
            <label className={getTeamClassName(game, 'HOME')}>
              {!game.home.team ? Multilingual(`GAME.TEAM_NONE`) : game.home.team}
            </label>
          </div>
          <label>{game.group}</label>
        </div>
      ));
  };

  return (
    <Fragment>
      <SchedulePanelDiv>
        <div className="schedule-panel schedule-panel--start">
          <Button width="unset" color="transparentWithBorder" nowrap onClick={handleGameFilterClick}>
            {Multilingual(`TOURNAMENT.FILTER`)}
          </Button>
          <label>
            {`${Multilingual(`TOURNAMENT.GAME_GROUP`)}：`}
            {selectedGroups.length === 0 ? Multilingual(`TOURNAMENT.GAME_SELECT_ALL`) : selectedGroups.join(', ')}
          </label>
          <label>
            {`${Multilingual(`TOURNAMENT.DATE_RANGE`)}：`}
            {!selectedDateRange[0] && !selectedDateRange[1]
              ? Multilingual(`TOURNAMENT.GAME_SELECT_ALL`)
              : `${!selectedDateRange[0] ? '' : moment(selectedDateRange[0]).format('YYYY/MM/DD')} - ${
                  !selectedDateRange[1] ? '' : moment(selectedDateRange[1]).format('YYYY/MM/DD')
                }`}
          </label>
        </div>
        <div className="schedule-panel schedule-panel--end">
          <Button width="unset" color="transparentWithBorder" nowrap onClick={handleLocationCollapse}>{`${
            gameListCollapse ? 'V' : 'X'
          } ${Multilingual(`CALENDAR.COLLAPSE`)}`}</Button>
        </div>
      </SchedulePanelDiv>
      <ListModeDiv>
        <div className="list-mode">
          {Object.keys(locationGames).map((locationUniqid) => (
            <div key={locationUniqid} className={`list-mode-item ${gameListCollapse ? 'collapse' : ''}`}>
              {/* 會有沒有地點的比賽，要避免報錯，也禁止在沒地點的地方新增比賽 */}
              <h3 className="list-mode-item--name">
                {find((l) => l.uniqid === locationUniqid, gameLocations)?.name}
                {locationUniqid && (
                  <Button
                    width="unset"
                    color="transparentWithBorder"
                    mini
                    onClick={handleTryCreateGame(null, '', locationUniqid)}
                  >
                    {Multilingual(`TOURNAMENT.CREATE_GAME`)}
                  </Button>
                )}
              </h3>
              <div className="list-mode-item--games">{getGamesInLocation(locationUniqid)}</div>
            </div>
          ))}
        </div>
      </ListModeDiv>
      <Modal.Modal size="lg" isOpen={isGameFilterOpen} outterClickBack onClickBack={handleGameFilterClick}>
        <Modal.ModalHeader hasBack onClickBack={handleGameFilterClick}>
          {Multilingual(`TOURNAMENT.FILTER`)}
        </Modal.ModalHeader>
        <Modal.ModalBody>
          <ModalFilterDiv>
            <div className="filter-item">
              <h3 className="filter-item--name">{Multilingual(`TOURNAMENT.GAME_GROUP`)}</h3>
              <div className="filter-item--options">
                <label>
                  <input
                    type="checkbox"
                    checked={selectedGroups.length === 0}
                    onChange={handleGameFilterOption('GROUP', 'ALL')}
                  />
                  {Multilingual(`TOURNAMENT.GAME_SELECT_ALL`)}
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={selectedGroups.includes('NONE')}
                    onChange={handleGameFilterOption('GROUP', 'NONE')}
                  />
                  {Multilingual(`TOURNAMENT.GAME_GROUP_NONE`)}
                </label>
                {groups.map((group) => (
                  <label key={group.name}>
                    <input
                      type="checkbox"
                      checked={selectedGroups.includes(group.name)}
                      onChange={handleGameFilterOption('GROUP', group.name)}
                    />
                    {group.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="filter-item">
              <h3 className="filter-item--name">{Multilingual(`TOURNAMENT.DATE_RANGE`)}</h3>
              <div className="filter-item--options">
                <label>
                  <input
                    name="all"
                    type="checkbox"
                    checked={!selectedDateRange[0] && !selectedDateRange[1]}
                    onChange={handleGameFilterDateRange}
                  />
                  {Multilingual(`TOURNAMENT.GAME_SELECT_ALL`)}
                </label>
                <label>
                  <input
                    name="start"
                    type="date"
                    value={!selectedDateRange[0] ? '' : moment(selectedDateRange[0]).format('YYYY-MM-DD')}
                    onChange={handleGameFilterDateRange}
                  />
                  ~
                  <input
                    name="end"
                    type="date"
                    value={!selectedDateRange[1] ? '' : moment(selectedDateRange[1]).format('YYYY-MM-DD')}
                    onChange={handleGameFilterDateRange}
                  />
                </label>
              </div>
            </div>
          </ModalFilterDiv>
        </Modal.ModalBody>
        <Modal.ModalFooter>
          <Button color="transparent" width="unset" onClick={handleGameFilterClick}>
            {Multilingual(`COMMON.CLOSE`)}
          </Button>
        </Modal.ModalFooter>
      </Modal.Modal>
    </Fragment>
  );
};

export default ScheduleLocation;
